import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import treeService from '../services/treeService';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../components/PageTransition'; // <-- NEW

// --- Smart Tree Data (Keeping your existing logic) ---
const TREE_RECOMMENDATIONS = {
  'Central Highlands (e.g., Kiambu, Nyeri)': {
    'Farm Field (Agroforestry)': [
      'Grevillea robusta (Mukima) - Deep roots, nitrogen fixing',
      'Croton megalocarpus (Mukinduri) - High carbon, indigenous',
      'Persea americana (Avocado) - Fruit & Carbon',
      'Markhamia lutea (Muu) - Soil stabilization'
    ],
    'Near Structure (Non-invasive roots)': [
      'Warburgia ugandensis (Muthiga) - Medicinal, safe roots',
      'Callistemon (Bottlebrush) - Ornamental, compact',
      'Citrus limon (Lemon) - Fruit, smaller root ball',
      'Portulacaria afra (Spekboom) - Massive carbon sponge'
    ],
    'Roadside / Open Space': [
      'Podocarpus falcatus (Podo) - High value timber',
      'Spathodea campanulata (Nandi Flame) - Fast growing',
      'Jacaranda mimosifolia - Ornamental carbon sink',
      'Bamboo (Oxytenanthera) - Rapid sequestration'
    ]
  },
  'Coastal Region (e.g., Mombasa, Kilifi)': {
    'Farm Field (Agroforestry)': [
      'Cocos nucifera (Coconut) - High utility',
      'Anacardium occidentale (Cashew) - Drought tolerant',
      'Moringa oleifera - Superfood, fast growth'
    ],
    'Near Structure (Non-invasive roots)': [
      'Frangipani (Plumeria) - Ornamental',
      'Mango (Grafted) - Controlled size',
      'Neem (Mwarubaini) - Air purification'
    ],
    'Roadside / Open Space': [
      'Casuarina equisetifolia - Windbreak, high carbon',
      'Baobab (Adansonia) - Massive carbon storage',
      'Terminalia catappa (Tropical Almond) - Shade'
    ]
  },
  'Arid / Semi-Arid (e.g., Machakos, Kajiado)': {
    'Farm Field (Agroforestry)': [
      'Melia volkensii (Mukau) - High value timber, dryland king',
      'Acacia tortilis - Nitrogen fixing',
      'Senna siamea - Fodder & fuel'
    ],
    'Near Structure (Non-invasive roots)': [
      'Aloe Barberae (Tree Aloe) - Sculptural, safe',
      'Moringa stenopetala - Food security',
      'Psidium guajava (Guava) - Fruit'
    ],
    'Roadside / Open Space': [
      'Acacia xanthophloea (Fever Tree) - Water lover',
      'Delonix regia (Flamboyant) - Shade',
      'Balanites aegyptiaca (Desert Date) - Hardwood'
    ]
  }
};

// Animation Variants for Staggered Grid
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1 // Delay between each child appearing
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const DashboardPage = () => {
  const [trees, setTrees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSpecies, setExpandedSpecies] = useState(null);

  // Form State
  const [region, setRegion] = useState('');
  const [site, setSite] = useState('');
  const [species, setSpecies] = useState('');
  const [date, setDate] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [formMessage, setFormMessage] = useState('');

  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    const fetchTrees = async () => {
      try {
        const data = await treeService.getMyTrees();
        setTrees(data);
        setIsLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || err.message);
        setIsLoading(false);
      }
    };
    fetchTrees();
  }, []);

  const handleCreateTree = async (e) => {
    e.preventDefault();
    setFormMessage('');
    try {
      const cleanSpecies = species.split(' - ')[0];
      const newTreeData = {
        species: cleanSpecies,
        plantingDate: date,
        location: { type: 'Point', coordinates: [parseFloat(lng), parseFloat(lat)] },
      };
      const createdTree = await treeService.createTree(newTreeData);
      setTrees([...trees, createdTree]);
      setFormMessage(`Tree planted! ID: ${createdTree.friendlyId}`);
      setDate(''); setLat(''); setLng('');
    } catch (err) {
      setFormMessage('Error planting tree.');
    }
  };

  //Helper function to get recommended trees
  const getRecommendedTrees = () => {
    if (region && site && TREE_RECOMMENDATIONS[region] && TREE_RECOMMENDATIONS[region][site]) {
      return TREE_RECOMMENDATIONS[region][site];
    }
    return [];
  };

  // --- Grouping Logic ---
  const groupedTrees = trees.reduce((acc, tree) => {
    const speciesName = tree.species;
    if (!acc[speciesName]) acc[speciesName] = [];
    acc[speciesName].push(tree);
    return acc;
  }, {});

  const renderTreeGroups = () => {
    if (isLoading) return <p className="text-center text-gray-500">Loading...</p>;
    if (trees.length === 0) return <p className="text-center text-gray-500">No trees yet. Plant your first one above!</p>;

    return (
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {Object.keys(groupedTrees).map((speciesName) => (
          <motion.div 
            key={speciesName} 
            variants={itemVariants} // Apply item animation
            className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
            layout
          >
            {/* Thumbnail Header */}
            <div 
              onClick={() => setExpandedSpecies(expandedSpecies === speciesName ? null : speciesName)}
              className="p-6 cursor-pointer bg-brand-white hover:bg-green-50 transition-colors flex justify-between items-center"
            >
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 rounded-full bg-brand-green-light flex items-center justify-center text-white font-bold text-xl shadow-sm">
                  {speciesName.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-brand-gray">{speciesName}</h3>
                  <p className="text-sm text-brand-green-dark font-medium">{groupedTrees[speciesName].length} Trees Planted</p>
                </div>
              </div>
              <div className="text-brand-gold font-bold text-xl">
                {expandedSpecies === speciesName ? '−' : '+'}
              </div>
            </div>

            {/* Expandable List */}
            <AnimatePresence>
              {expandedSpecies === speciesName && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-white border-t border-gray-100"
                >
                  {groupedTrees[speciesName].map((tree) => (
                    <Link 
                      to={`/tree/${tree._id}`} 
                      key={tree._id}
                      className="block p-4 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex justify-between items-center transition-colors"
                    >
                      <div>
                        <span className="font-mono text-brand-green-dark font-bold text-sm bg-green-100 px-2 py-1 rounded border border-brand-green-light">
                          {tree.friendlyId || 'PENDING-ID'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Planted: {new Date(tree.plantingDate).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-brand-gold hover:text-brand-gold-light font-bold text-sm">View Stats &rarr;</span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  return (
    <PageTransition> {/* <-- WRAPPER ADDED */}
      <div className="space-y-10">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-brand-green-dark mb-2">
            Welcome, {user ? user.name.split(' ')[0] : 'Eco-Warrior'}!
          </h1>
          <p className="text-gray-600">Select region and site to plant, or view your species portfolios below.</p>
        </div>

        {/* Intelligent Form */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-brand-green mb-4 flex items-center">
            🌱 Plant a New Tree
          </h2>
          {formMessage && <p className="mb-4 text-brand-green-dark font-medium">{formMessage}</p>}
          
          <form onSubmit={handleCreateTree} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none bg-white" value={region} onChange={(e) => { setRegion(e.target.value); setSite(''); setSpecies(''); }} required>
              <option value="">-- Select Region --</option>
              {Object.keys(TREE_RECOMMENDATIONS).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none bg-white" value={site} onChange={(e) => { setSite(e.target.value); setSpecies(''); }} disabled={!region} required>
              <option value="">-- Select Planting Site --</option>
              {region && Object.keys(TREE_RECOMMENDATIONS[region]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none bg-white md:col-span-2" value={species} onChange={(e) => setSpecies(e.target.value)} disabled={!site} required>
              <option value="">-- Recommended High-Carbon Species --</option>
              {region && site && TREE_RECOMMENDATIONS[region][site].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="date" className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none" value={date} onChange={(e) => setDate(e.target.value)} required />
            <input type="number" placeholder="Latitude" step="any" className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none" value={lat} onChange={(e) => setLat(e.target.value)} required />
            <input type="number" placeholder="Longitude" step="any" className="p-2 border rounded focus:ring-2 focus:ring-brand-green outline-none" value={lng} onChange={(e) => setLng(e.target.value)} required />
            <button type="submit" className="md:col-span-2 bg-brand-green hover:bg-brand-green-dark text-white font-bold py-2 rounded transition-colors duration-200 shadow-sm">Add to Portfolio</button>
          </form>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-brand-gray mb-6 border-b-2 border-brand-gold inline-block pb-1">
            Your Species Portfolio
          </h2>
          {renderTreeGroups()}
        </div>
      </div>
    </PageTransition>
  );
};

export default DashboardPage;